import React, { useMemo, useRef } from 'react';
import { Box, Stack } from '@mui/material';

import type { Units } from 'types/units';
import type { SwellsTooltipData } from 'types/graphs';

import SwellReading from './SwellReading';

import styles from './SwellTooltip.module.scss';

interface Props {
  activeWave: SwellsTooltipData;
  activeSwell: number;
  setActiveSwell: (index: number) => void;
  graphWidth: number;
  units: Units;
}
export const SWELL_CARD_PADDING = 20;

const SwellTooltip: React.FC<Props> = ({
  activeWave,
  activeSwell,
  setActiveSwell,
  graphWidth,
  units,
}) => {
  const swells = activeWave?.data?.swells?.sort((a, b) => a.index - b.index);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const cardWidth = cardRef?.current?.offsetWidth || 0;

  const positionStyles = useMemo(() => {
    let leftPosition = 0;
    if (activeWave?.leftDayPosition >= 0) {
      leftPosition = activeWave.leftDayPosition;
    } else if (activeWave?.leftPosition >= 0) {
      leftPosition = Math.min(activeWave.leftPosition - SWELL_CARD_PADDING, graphWidth - cardWidth);
    }
    return { left: Math.max(leftPosition, 0), marginLeft: 0 };
  }, [activeWave.leftDayPosition, activeWave.leftPosition, cardWidth, graphWidth]);

  const filteredSwells = useMemo(
    () =>
      swells
        ?.filter((swell) => swell?.period > 0)
        ?.map((swell) => (
          <SwellReading
            activeSwell={activeSwell}
            applySwellColors
            key={swell.index}
            setActiveSwell={setActiveSwell}
            swell={swell}
            units={units}
          />
        )),
    [activeSwell, setActiveSwell, swells, units],
  );

  return (
    <Box className={styles.swellTooltipContainer} data-testid="swell-graph-tooltip">
      {activeWave?.data?.swells && (
        <Stack
          className={styles.swellTooltipContent}
          data-testid="swell-graph-tooltip-content"
          direction="row"
          ref={cardRef}
          spacing="5px"
          style={positionStyles}
        >
          {filteredSwells}
        </Stack>
      )}
    </Box>
  );
};

export default SwellTooltip;
